/**
 * SourceImpactHost — mounted once by the shell; owns the open/closed state of
 * the impacted-sections panel and listens on the {@link openSourceImpact} bus
 * so any entry point (library document detail, chat step) can open it.
 *
 * Renders nothing while no run is open (``runId`` null) and does not register
 * on the bus then, so entry points hide themselves.
 */

import type React from "react";
import { useCallback, useEffect, useState } from "react";
import type { ActuallyStartRunOpts } from "../hooks/useDraftFlow";
import type { SpineSection } from "../hooks/useVariantCRun";
import { surfaceColors } from "../tokens";
import { registerSourceImpactHost } from "./sourceImpactBus";
import { type RedraftHandler, type RedraftOutcome, SourceImpactPanel } from "./SourceImpactPanel";

/** The shell's run-start primitive (``useDraftFlow().actuallyStartRun``). */
export type StartRunFn<S> = (
  selection: S,
  planId: string | null,
  moduleIds: string[] | null,
  continueRunId: string | null,
  opts?: ActuallyStartRunOpts,
) => Promise<void>;

/**
 * Build the panel's redraft handler on the shell's existing continue-sections
 * path: ``actuallyStartRun(selection, null, module_ids, runId, {runInstructions})``
 * — the same call "Draft more" / "Generate" make, so the save-before-redraft,
 * draft pointer, live projection, SSE re-subscribe and spine all behave the
 * same. The payment gate is skipped like the Inspector's "Generate": these
 * sections are already drafted in this run, and the server gate stays the
 * backstop.
 */
export function makeRedraftHandler<S>(
  startRun: StartRunFn<S>,
  selection: S | null | undefined,
  runId: string | null | undefined,
): RedraftHandler {
  return async (req) => {
    if (!selection || !runId) return { status: "not_started" };
    let outcome: RedraftOutcome = { status: "not_started" };
    await startRun(selection, null, req.module_ids, runId, {
      skipPaymentGate: true,
      runInstructions: req.run_instructions,
      onStarted: () => {
        outcome = { status: "started" };
      },
      onError: (error) => {
        outcome = { status: "failed", error };
      },
    });
    return outcome;
  };
}

export interface SourceImpactHostProps {
  runId: string | null;
  onRedraft: RedraftHandler;
  spine?: readonly SpineSection[];
}

export const SourceImpactHost: React.FC<SourceImpactHostProps> = ({ runId, onRedraft, spine }) => {
  const [documentName, setDocumentName] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) return;
    return registerSourceImpactHost((name) => setDocumentName(name));
  }, [runId]);

  // A different run closes the panel.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on run change only
  useEffect(() => {
    setDocumentName(null);
  }, [runId]);

  const close = useCallback(() => setDocumentName(null), []);

  useEffect(() => {
    if (!documentName) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [documentName, close]);

  if (!runId || !documentName) return null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: non-modal side panel over the task pane; Escape handled on document
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click closes; Escape handled on document
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Impacted sections"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "flex",
        justifyContent: "flex-end",
        background: "rgba(15, 10, 30, 0.18)",
      }}
      onClick={close}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled on document */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "460px",
          height: "100%",
          overflowY: "auto",
          boxSizing: "border-box",
          padding: "14px",
          background: surfaceColors.paper,
          boxShadow: "-8px 0 30px rgba(0, 0, 0, 0.12)",
        }}
      >
        <SourceImpactPanel
          runId={runId}
          documentName={documentName}
          onRedraft={onRedraft}
          spine={spine}
          onClose={close}
        />
      </div>
    </div>
  );
};

export default SourceImpactHost;
